import { ReactiveControllerHost, CSSResultGroup, CSSResult } from 'lit';

/**
 * The LightComStyles ReactiveController.
 *
 * This controller allows components to inject styles into the light-dom.
 *
 * @param host The host element
 */

export class LightDomStyles {
  host: ReactiveControllerHost & Element;
  cssStyles: CSSResultGroup;

  constructor(
    host: ReactiveControllerHost & Element,
    cssStyles: CSSResultGroup
  ) {
    // Store a reference to the host
    this.host = host;
    // Store the css styles to be injected.
    this.cssStyles = cssStyles;
    // Register for lifecycle updates
    host.addController(this);
  }

  hostConnected(): void {
    this._addLightDomStyles(this.cssStyles);
  }

  /**
   * Add styles from a CSSResult to the light-dom, scoped by the scopeId.
   * Only add if it doesn't already exist.
   */
  _addLightDomStyle(scopeId: string, cssStyles: CSSResult, index = 0) {
    const elementId = index !== 0 ? scopeId + '-' + index : scopeId;
    if (!document.getElementById(elementId)) {
      const scopedStyleElement = document.createElement('style');
      scopedStyleElement.id = elementId;
      scopedStyleElement.innerHTML = `${cssStyles}`;
      // Prepend so consumer theme can still override.
      document.head.prepend(scopedStyleElement);

      // Add the scopeId to each CSS rule.
      const stylesheet = scopedStyleElement.sheet;
      if (stylesheet) {
        let newStyleText = '';
        for (let i = 0; i < stylesheet.cssRules.length; i++) {
          const rule = stylesheet.cssRules[i];
          if (!(rule instanceof CSSStyleRule)) {
            continue;
          }
          // A rule can have multiple selectors, so split on comma.
          const selectorText = Array.from(rule.selectorText.split(',')).map(
            selector => {
              // Replace :host with the name of the component.
              if (selector.includes(':root')) {
                return selector;
              } else if (selector.includes(':host(')) {
                return selector.replace(':host(', scopeId).replace(')', '');
              } else if (selector.includes(':host')) {
                return selector.replace(':host', scopeId);
              }
              // Convert a ::slotted rule to the component name selector.
              else if (selector.includes('::slotted(')) {
                return (
                  scopeId +
                  ' ' +
                  selector.replace('::slotted(', '').replace(')', '')
                );
              }
              // Otherwise, just prefix selector with component name.
              return scopeId + ' ' + selector;
            }
          );
          rule.selectorText = selectorText.join(',');
          newStyleText = newStyleText + '\n' + rule.cssText;
          //console.log(rule.cssText);
        }
        // The above rule changes take affect in the browser, but the
        // css text in the document.head will still show the previous string.
        // Update the document.head string value here to help with debugging.
        scopedStyleElement.innerHTML = newStyleText;
      }
    }
  }

  /**
   * Add styles from a CSSResultGroup to the light-dom.
   */
  _addLightDomStyles(cssStyles: CSSResultGroup) {
    const scopeId = this.host.tagName.toLowerCase();
    if (cssStyles instanceof Array) {
      // Loop through style array backwards since addLightDomStyle uses
      // prepend and we want to keep the original order.
      for (let index = cssStyles.length - 1; index >= 0; index--) {
        const item = cssStyles[index];
        if (item instanceof CSSResult) {
          this._addLightDomStyle(scopeId, item, index);
        }
      }
    } else if (cssStyles instanceof CSSResult) {
      this._addLightDomStyle(scopeId, cssStyles);
    }
  }
}
